/**
 * UI state and configuration type definitions
 */

import type { PeriodType, MonthValue, YearValue } from './FinancialMetrics';

/**
 * Date selection state
 */
export interface DateSelection {
  month: MonthValue;
  year: YearValue;
}

/**
 * Timeline range selection (using 2-digit years)
 */
export interface TimelineRange {
  leftYear: string;  // e.g., '19', '20', '23'
  rightYear: string; // e.g., '23', '25'
}

/**
 * Tab types in the application
 */
export type TabType = 'financial' | 'trading' | 'operating' | 'business';

/**
 * Tab identifier with suffix
 */
export type TabId = `${TabType}-tab`;

/**
 * Snap point for timeline selector
 */
export interface SnapPoint {
  position: number;
  year: string;
}

/**
 * Toggle state for business descriptions
 */
export type ToggleState = 'short' | 'full';

/**
 * Toggle type for business description fields
 */
export type ToggleType = 'description' | 'headquarters';

/**
 * Application configuration
 */
export interface AppConfig {
  defaultMonth: MonthValue;
  defaultYear: YearValue;
  defaultTimelineLeft: string;
  defaultTimelineRight: string;
  defaultPeriodType: PeriodType;
}

/**
 * Favorite page definition
 */
export interface FavoritePage {
  path: string;
  title: string;
}

/**
 * Saved M&A Comp Set definition
 */
export interface SavedMACompSet {
  name: string;
  transactionIds: string[];
  savedAt: string; // ISO timestamp
}

/**
 * Storage keys for localStorage and sessionStorage
 */
export enum StorageKey {
  TIMELINE_RANGE = 'timelineRange',
  PERIOD_TYPE = 'periodType',
  FAVORITES = 'favorites',
  SELECTED_MA_TRANSACTIONS = 'selectedMATransactions',
  SAVED_MA_COMP_SET = 'savedMACompSet',
}

/**
 * Content strategy type for different timeline ranges
 */
export type ContentStrategyType = 'range-19-25' | 'range-20-23' | 'range-13-23' | 'default';
